import React, {useContext, useEffect, useRef, useState} from 'react';
import FirmwareController from "./generated/controllers/firmwareController";
import PagedSearchTable, {PagedTableFunctions} from "./components/PagedSearchTable";
import {EditRow, EditTable} from "./components/Fields";
import Input from "./components/Input";
import Dialog from "./components/Dialog";
import FirmwareVersion from "./generated/interfaces/firmwareVersion";
import {useValidation} from "./validation";
import Success from "./components/Success";
import AppContext from "./pages/appContext";
import {dateFormat} from "./date";
import DocsFileUploader from "./components/DocsFileUploader";
import FirmwareUploadRequest from "./generated/interfaces/firmwareUploadRequest";
import FirmwareUpdateRequest from "./generated/interfaces/firmwareUpdateRequest";

const emptyFirmwareUploadRequest = ():  FirmwareUploadRequest => {
    return {
        description: "", 
        version: "",
        file: null
    }
}
const emptyFirmwareUpdateRequest = ():  FirmwareUpdateRequest => {
    return {
        description: "", 
        id: 0, 
    }
}

const FirmwareManagement = () => {
    const context = useContext(AppContext);
    const pagedTableRef = useRef<PagedTableFunctions<FirmwareVersion>>()
    const [allFirmwareVersions, setAllFirmwareVersions] = useState<string[]>([])
    
    const [insertFirmwareModal, setInsertFirmwareModal] = useState(false)
    const [insertFirmware, setInsertFirmware]= useState<FirmwareUploadRequest>(emptyFirmwareUploadRequest())
    
    const [editFirmwareModal, setEditFirmwareModal] = useState(false)
    const [selectedFirmware, setSelectedFirmware]= useState<FirmwareUpdateRequest>(emptyFirmwareUpdateRequest())

    useEffect(() => {
        FirmwareController.allVersions().then(resp => {
            setAllFirmwareVersions(resp)
        })
    }, [])

    function isUniqueVersion() {
        return !allFirmwareVersions.some(v => v.toLowerCase().trim() == insertFirmware.version.toLowerCase().trim())
    }

    const validationInsert = useValidation({
        version: () => insertFirmware.version !== null && insertFirmware.version.length > 0 && isUniqueVersion(), // also have to check uniqueness here
        description: () => insertFirmware.description !== null && insertFirmware.description.length > 0,
        file: () => insertFirmware.file !== null,
    })

    const validationUpdate = useValidation({
        description: () => selectedFirmware.description !== null && selectedFirmware.description.length > 0,
    })

    function addFirmware() {
        if (!validationInsert.validate()) return

        const hideLoader = context.showLoader()
        FirmwareController.insert({
            file: insertFirmware.file,
            description: insertFirmware.description,
            version: insertFirmware.version.trim(),
        }).then(r => {
            resetInsertFirmware()
            context.showSnack(<Success title='Success' text='success'/>)
            pagedTableRef.current?.refresh();
        }).finally(() => hideLoader())
    }

    function updateFirmware() {
        if (!validationUpdate.validate()) return

        const hideLoader = context.showLoader()
        FirmwareController.update({
            id: selectedFirmware.id,
            description: selectedFirmware.description,
        }).then(r => {
            resetEditFirmware()
            context.showSnack(<Success title='Success' text='success'/>)
            pagedTableRef.current?.refresh();
        }).finally(() => hideLoader())
    }

    function handleUpload(file: File | null) {
        setInsertFirmware({...insertFirmware, file: file})
    }

    function editSelectedFirmware(item: FirmwareVersion) {
        setSelectedFirmware({
            description: item.description,
            id: item.id,
        })
        setEditFirmwareModal(true)
    }

    const resetInsertFirmware = () => {
        setInsertFirmwareModal(false)
        setInsertFirmware(emptyFirmwareUploadRequest());
        validationInsert.clear();
    };

    const resetEditFirmware = () => {
        setEditFirmwareModal(false)
        setSelectedFirmware(emptyFirmwareUpdateRequest());
        validationUpdate.clear();
    };
    
    return (
        <div>
            <button className="btn btn-primary my-2" 
                    onClick={() => setInsertFirmwareModal(true)}>
                Add Firmware
            </button>
            <PagedSearchTable
                componentRef={pagedTableRef}
                call={FirmwareController.paged} 
                columns={[
                {
                    header: 'Version',
                    row: (row) => <span>{row.version}</span>
                },
                {
                    header: 'Description',
                    row: (row) => <span>{row.description}</span>
                },
                {
                    header: 'Release Date',
                    row: (row) => <span>{dateFormat(row.releaseDate, '%d %M %Y')}</span>
                },
                {
                    header: 'File Name',
                    row: (row) => <span>{row.fileName}</span>
                },
                {
                    header: <div className={"text-right"}>Actions</div>,
                    key: 'actions',
                    row: (item) => <div className='flex items-center justify-end'>
                        <div className="btn-sm text-right bg-primary p-1 text-white rounded" onClick={() => editSelectedFirmware(item)}>edit</div>
                    </div>
                },
            ]}/>

            <Dialog title="Add Firmware" setShow={setInsertFirmwareModal} show={insertFirmwareModal} body={
                <EditTable discard={resetInsertFirmware}
                           save={() => addFirmware()}
                           saveWord= {'add'}>

                    {EditRow('Version:', <Input value={insertFirmware.version ?? ''} change={v => setInsertFirmware({...insertFirmware, version: v})}/>, validationInsert.rules.version, 'Unique version required')}
                    {EditRow('Description:', <Input value={insertFirmware.description ?? ''} change={v => setInsertFirmware({...insertFirmware, description: v})}/>, validationInsert.rules.description, 'Description required')}
                    {EditRow('File:', <DocsFileUploader file={insertFirmware.file} setFile={handleUpload}/>, validationInsert.rules.file, 'File required')}
                </EditTable>
            }/>

            <Dialog title="Edit Firmware" setShow={setEditFirmwareModal} show={editFirmwareModal} body={
                <EditTable discard={resetEditFirmware}
                           save={() => updateFirmware()}
                           saveWord= {'update'}>

                    {EditRow('Description:', <Input value={selectedFirmware.description ?? ''} change={v => setSelectedFirmware({...selectedFirmware, description: v})}/>, validationUpdate.rules.description, 'Description required')}
                </EditTable>
            }/>
            
        </div>
    );
};

export default FirmwareManagement;